import React, { useState } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import Modal from './Modal';
import Toast from './Toast';
import useFormCRUD from '../hooks/useFormCRUD';

/**
 * Business management with full-width table and modal-based Add/Edit forms.
 * Supports parent-child hierarchy via parent_id dropdown.
 *
 * @param {Object} props
 * @param {Function} [props.onUpdate] - Callback invoked after create/delete/toggle operations
 * @returns {JSX.Element} The business table with Add/Edit modals
 */
function BusinessForm({ onUpdate }) {
  const [businessSearch, setBusinessSearch] = useState('');

  const initialFormData = {
    name: '',
    address: '',
    tax_id: '',
    contact_info: '',
    primary_contact_name: '',
    primary_contact_phone: '',
    primary_contact_email: '',
    is_default: false,
    parent_id: ''
  };

  const {
    items: businesses,
    formData,
    setFormData,
    editingId,
    showForm,
    setShowForm,
    submitting,
    toast,
    confirmModal,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleToggleStatus,
    handleCloseToast,
    handleCloseConfirmModal,
  } = useFormCRUD({
    apiGetAll: () => api.getBusinesses({ include_inactive: true }),
    apiCreate: api.createBusiness,
    apiUpdate: api.updateBusiness,
    apiDelete: api.deleteBusiness,
    apiToggleStatus: api.toggleBusinessStatus,
    initialFormData,
    entityName: 'Business',
    useConfirmModal: true,
    getItemDisplayName: (item) => item.name,
    onUpdate,
  });

  const filteredBusinesses = businessSearch.trim()
    ? businesses.filter(b =>
        b.name.toLowerCase().includes(businessSearch.toLowerCase()) ||
        (b.primary_contact_name || '').toLowerCase().includes(businessSearch.toLowerCase())
      )
    : businesses;

  const activeCount = businesses.filter(b => b.status === 'active').length;

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={handleCloseToast} />}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          description={confirmModal.description}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={handleCloseConfirmModal}
        />
      )}

      {/* Add Business Modal */}
      <Modal
        isOpen={showForm && !editingId}
        onClose={() => setShowForm(false)}
        title="Add Business"
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="business-name">Business Name <span className="req">*</span></label>
            <input
              id="business-name"
              className="input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="business-tax-id">Tax ID / EIN</label>
            <input
              id="business-tax-id"
              className="input"
              type="text"
              value={formData.tax_id}
              onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="business-parent">Parent Business</label>
            <select
              id="business-parent"
              className="input"
              value={formData.parent_id}
              onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
            >
              <option value="">None (standalone)</option>
              {businesses
                .filter(b => !b.parent_id && b.status === 'active' && b.id !== editingId)
                .map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))
              }
            </select>
            <span className="hint">Select a parent to create as a child location.</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="business-address">Address</label>
            <textarea
              id="business-address"
              className="input"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              rows={2}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="business-contact-name">Contact Name</label>
            <input
              id="business-contact-name"
              className="input"
              type="text"
              value={formData.primary_contact_name}
              onChange={(e) => setFormData({...formData, primary_contact_name: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="business-contact-phone">Phone</label>
            <input
              id="business-contact-phone"
              className="input"
              type="tel"
              value={formData.primary_contact_phone}
              onChange={(e) => setFormData({...formData, primary_contact_phone: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="business-contact-email">Email</label>
            <input
              id="business-contact-email"
              className="input"
              type="email"
              value={formData.primary_contact_email}
              onChange={(e) => setFormData({...formData, primary_contact_email: e.target.value})}
            />
          </div>

          <div className="settings-form__settings-group">
            <label className="settings-form__checkbox">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
              />
              Set as default business
            </label>
          </div>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Add Business'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Business Modal */}
      <Modal
        isOpen={!!editingId}
        onClose={() => { handleCancelEdit(); setShowForm(false); }}
        title="Edit Business"
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="edit-business-name">Business Name <span className="req">*</span></label>
            <input
              id="edit-business-name"
              className="input"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-business-tax-id">Tax ID / EIN</label>
            <input
              id="edit-business-tax-id"
              className="input"
              type="text"
              value={formData.tax_id}
              onChange={(e) => setFormData({...formData, tax_id: e.target.value})}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-business-parent">Parent Business</label>
            <select
              id="edit-business-parent"
              className="input"
              value={formData.parent_id}
              onChange={(e) => setFormData({...formData, parent_id: e.target.value})}
            >
              <option value="">None (standalone)</option>
              {businesses
                .filter(b => !b.parent_id && b.status === 'active' && b.id !== editingId)
                .map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))
              }
            </select>
            <span className="hint">Select a parent to create as a child location.</span>
            {editingId && formData.parent_id && businesses.some(b => b.parent_id === editingId) && (
              <span className="business-form__warning">
                Warning: This business has children. Setting a parent may create a circular dependency.
              </span>
            )}
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-business-address">Address</label>
            <textarea
              id="edit-business-address"
              className="input"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              rows={2}
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-business-contact-name">Contact Name</label>
            <input
              id="edit-business-contact-name"
              className="input"
              type="text"
              value={formData.primary_contact_name}
              onChange={(e) => setFormData({...formData, primary_contact_name: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="edit-business-contact-phone">Phone</label>
            <input
              id="edit-business-contact-phone"
              className="input"
              type="tel"
              value={formData.primary_contact_phone}
              onChange={(e) => setFormData({...formData, primary_contact_phone: e.target.value})}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="edit-business-contact-email">Email</label>
            <input
              id="edit-business-contact-email"
              className="input"
              type="email"
              value={formData.primary_contact_email}
              onChange={(e) => setFormData({...formData, primary_contact_email: e.target.value})}
            />
          </div>

          <div className="settings-form__settings-group">
            <label className="settings-form__checkbox">
              <input
                type="checkbox"
                checked={formData.is_default}
                onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
              />
              Set as default business
            </label>
          </div>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { handleCancelEdit(); setShowForm(false); }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <div className="card">
        <div className="settings-form__table-header">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            Businesses ({activeCount} active{businesses.length !== activeCount ? `, ${businesses.length - activeCount} inactive` : ''})
          </h3>
          <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
            {businesses.length > 3 && (
              <input
                className="input settings-form__search-input"
                type="text"
                placeholder="Search..."
                value={businessSearch}
                onChange={(e) => setBusinessSearch(e.target.value)}
                aria-label="Search businesses"
              />
            )}
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Add Business</button>
          </div>
        </div>

        {businesses.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">No businesses yet</div>
            <div className="empty-state__description">Add your first business to get started.</div>
          </div>
        ) : (
          <div className="tbl-wrap tbl-wrap--md">
            <table>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Parent</th>
                  <th scope="col">Contact</th>
                  <th scope="col">Status</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredBusinesses.map(b => {
                  const parentBiz = b.parent_id ? businesses.find(p => p.id === b.parent_id) : null;
                  return (
                    <tr
                      key={b.id}
                      className={`${b.status === 'inactive' ? 'row-inactive' : ''} ${editingId === b.id ? 'table-row--editing' : ''}`.trim() || undefined}
                    >
                      <td>
                        <div>
                          <span style={{ fontWeight: 500 }}>
                            {b.name}{b.is_default ? ' (Default)' : ''}
                          </span>
                          {b.tax_id && <div className="settings-form__meta">{b.tax_id}</div>}
                        </div>
                      </td>
                      <td>
                        {parentBiz ? parentBiz.name : <span className="settings-form__meta">&mdash;</span>}
                      </td>
                      <td>
                        {b.primary_contact_name && <div>{b.primary_contact_name}</div>}
                        {b.primary_contact_phone && <div className="settings-form__meta">{b.primary_contact_phone}</div>}
                        {!b.primary_contact_name && !b.primary_contact_phone && <span className="settings-form__meta">&mdash;</span>}
                      </td>
                      <td>
                        <span className={`status-badge status-${b.status}`}>
                          {b.status === 'active' ? '\u2713' : '\u2717'} {b.status}
                        </span>
                      </td>
                      <td>
                        <div className="settings-form__action-btns">
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleEdit(b)}
                            aria-label={'Edit ' + b.name}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleToggleStatus(b.id)}
                            aria-label={(b.status === 'active' ? 'Disable ' : 'Enable ') + b.name}
                            style={{ color: b.status === 'active' ? 'var(--warning)' : 'var(--success)' }}
                          >
                            {b.status === 'active' ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => handleDelete(b.id, b.name)}
                            aria-label={'Delete ' + b.name}
                            style={{ color: 'var(--error)' }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

BusinessForm.propTypes = {
  /** Callback invoked after create/delete/toggle operations */
  onUpdate: PropTypes.func,
};

export default BusinessForm;
